import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { commissionsService } from '../../services';
import commissionMessagesService from '../../services/commissionMessagesService';
import { useResponsive } from '../../hooks/useResponsive';
import CommissionForm from './CommissionForm';
import TaxForms from './TaxForms/TaxForms';
import CommissionMessagingModal from './CommissionMessagingModal';
import travelieTheme from '../../styles/travelieTheme';

// Phase 7: Migrated to TravelieTheme - June 16, 2026

const CommissionsList = () => {
  const { user } = useAuth();
  const { isDesktopOrWide, isMobile } = useResponsive();
  const isIndependentAgent = user?.role === 'independent_agent';
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [showTaxForms, setShowTaxForms] = useState(false);
  const [showMessagingModal, setShowMessagingModal] = useState(false);
  const [selectedCommissionForMessaging, setSelectedCommissionForMessaging] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const data = await commissionsService.getAll();
      setCommissions(data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCommissionSubmit = (newCommission) => {
    fetchCommissions(); // Refresh the list
    setShowForm(false);
  };

  const handleMessageSent = () => {
    // Refresh commissions to update status
    fetchCommissions();
    setShowMessagingModal(false);
    setSelectedCommissionForMessaging(null);
  };

  const handleStatusUpdate = async (commissionId, newStatus) => {
    try {
      setError('');
      await commissionsService.updateOwnStatus(commissionId, newStatus);
      // Refresh commissions list
      await fetchCommissions();
    } catch (error) {
      console.error('Failed to update status:', error);
      setError(error.response?.data?.error || 'Failed to update commission status');
    }
  };

  // Helper function for status badge styling
  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
      fontSize: travelieTheme.typography.fontSize.xs,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      display: 'inline-block',
      textTransform: 'capitalize'
    };

    const statusStyles = {
      approved: { ...baseStyle, background: '#DBEAFE', color: '#1E40AF' },
      paid: { ...baseStyle, background: '#DCFCE7', color: '#166534' },
      pending: { ...baseStyle, background: '#FEF3C7', color: '#92400E' },
      denied: { ...baseStyle, background: '#FEE2E2', color: '#991B1B' },
      on_hold: { ...baseStyle, background: '#F3E8FF', color: '#6B21A8' },
      needs_reply: { ...baseStyle, background: '#FED7AA', color: '#9A3412' },
      needs_agent_reply: { ...baseStyle, background: '#FED7AA', color: '#9A3412' },
      needs_admin_reply: { ...baseStyle, background: '#FECACA', color: '#7F1D1D' }
    };

    return statusStyles[status] || statusStyles.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Use string splitting to avoid timezone conversion issues
    // Handle both ISO format (T separator) and MySQL format (space separator)
    const datePart = dateString.split(/[T ]/)[0]; // Get YYYY-MM-DD part
    const [year, month, day] = datePart.split('-');
    const date = new Date(year, month - 1, day); // Create date in local timezone
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Filter commissions based on status, search term, and date range
  const filteredCommissions = commissions.filter(commission => {
    // Status filter
    if (filter !== 'all' && commission.status !== filter) return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        commission.reservation_number?.toLowerCase().includes(search) ||
        commission.guest_first_name?.toLowerCase().includes(search) ||
        commission.guest_last_name?.toLowerCase().includes(search) ||
        commission.supplier?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    // Date range filter
    if (dateRange.start && new Date(commission.travel_date) < new Date(dateRange.start)) return false;
    if (dateRange.end && new Date(commission.travel_date) > new Date(dateRange.end)) return false;
    
    return true;
  });

  // Calculate statistics
  const stats = {
    total: commissions.length,
    pending: commissions.filter(c => c.status === 'pending').length,
    paid: commissions.filter(c => c.status === 'paid').length,
    totalGross: commissions.reduce((sum, c) => sum + parseFloat(c.gross_commission || 0), 0),
    totalAgent: commissions.reduce((sum, c) => sum + parseFloat(c.agent_commission || 0), 0),
    paidAgent: commissions
      .filter(c => c.status === 'paid')
      .reduce((sum, c) => sum + parseFloat(c.agent_commission || 0), 0)
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: travelieTheme.colors.text.secondary
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `4px solid ${travelieTheme.colors.neutral.border}`,
          borderTopColor: travelieTheme.colors.primary.main,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{
          marginTop: travelieTheme.spacing.md,
          fontSize: travelieTheme.typography.fontSize.sm,
          color: travelieTheme.colors.text.secondary
        }}>
          Loading commissions...
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: travelieTheme.spacing.lg, maxWidth: "1400px", margin: "0 auto" }}>
      {/* Error Display */}
      {error && (
        <div style={{ marginBottom: travelieTheme.spacing.md, background: "#FEE2E2", border: "1px solid #FCA5A5", color: "#991B1B", padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`, borderRadius: travelieTheme.components.card.borderRadius }}>
          {error}
        </div>
      )}
      
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: travelieTheme.spacing['3xl']
      }}>
        <div>
          <h2 style={{
            fontSize: travelieTheme.typography.fontSize['2xl'],
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.xs,
            margin: 0
          }}>
            Commission Management
          </h2>
          <p style={{
            fontSize: travelieTheme.typography.fontSize.sm,
            color: travelieTheme.colors.text.secondary,
            marginTop: '4px'
          }}>
            {isIndependentAgent ? 'Track and manage your commissions' : 'Track and manage your commission requests'}
          </p>
        </div>
        <div style={{
          display: 'flex',
          gap: travelieTheme.spacing.md
        }}>
          {user?.role !== 'independent_agent' && (
            <button 
              onClick={() => setShowTaxForms(true)}
              style={{
                background: '#64748B',
                color: travelieTheme.components.button.primary.color,
                borderRadius: travelieTheme.components.button.primary.borderRadius,
                padding: travelieTheme.components.button.primary.padding,
                height: travelieTheme.components.button.primary.height,
                fontSize: travelieTheme.components.button.primary.fontSize,
                fontWeight: travelieTheme.components.button.primary.fontWeight,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: travelieTheme.spacing.sm,
                transition: `background ${travelieTheme.transitions.normal}`
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#64748B'}
            >
              <svg style={{ height: '20px', width: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Tax Forms
            </button>
          )}
          <button 
            onClick={() => setShowForm(true)}
            style={{
              background: travelieTheme.components.button.primary.background,
              color: travelieTheme.components.button.primary.color,
              borderRadius: travelieTheme.components.button.primary.borderRadius,
              padding: travelieTheme.components.button.primary.padding,
              height: travelieTheme.components.button.primary.height,
              fontSize: travelieTheme.components.button.primary.fontSize,
              fontWeight: travelieTheme.components.button.primary.fontWeight,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: travelieTheme.spacing.sm,
              transition: `background ${travelieTheme.transitions.normal}`
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#2563EB'}
            onMouseLeave={(e) => e.currentTarget.style.background = travelieTheme.components.button.primary.background}
          >
            <svg style={{ height: '20px', width: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Submit New Commission
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div 
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
          gap: travelieTheme.spacing.lg,
          marginBottom: travelieTheme.spacing['3xl']
        }}
      >
        <div style={{
          background: travelieTheme.colors.neutral.card,
          borderRadius: travelieTheme.components.statCard.borderRadius,
          padding: travelieTheme.components.statCard.padding,
          boxShadow: travelieTheme.components.statCard.shadow,
          display: 'flex',
          alignItems: 'center',
          gap: travelieTheme.spacing.lg
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: travelieTheme.components.statCard.iconBorderRadius,
            background: travelieTheme.colors.primary.light,
            color: travelieTheme.colors.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{
              fontSize: travelieTheme.typography.fontSize.sm,
              color: travelieTheme.colors.text.secondary,
              marginBottom: '4px',
              margin: 0
            }}>
              Total Commissions
            </p>
            <p style={{
              fontSize: travelieTheme.typography.fontSize['2xl'],
              fontWeight: travelieTheme.typography.fontWeight.bold,
              color: travelieTheme.colors.text.primary,
              margin: 0
            }}>
              {stats.total}
            </p>
          </div>
        </div>

        <div style={{
          background: travelieTheme.colors.neutral.card,
          borderRadius: travelieTheme.components.statCard.borderRadius,
          padding: travelieTheme.components.statCard.padding,
          boxShadow: travelieTheme.components.statCard.shadow,
          display: 'flex',
          alignItems: 'center',
          gap: travelieTheme.spacing.lg
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: travelieTheme.components.statCard.iconBorderRadius,
            background: '#FEF3C7',
            color: '#F59E0B',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{
              fontSize: travelieTheme.typography.fontSize.sm,
              color: travelieTheme.colors.text.secondary,
              marginBottom: '4px',
              margin: 0
            }}>
              Pending Approval
            </p>
            <p style={{
              fontSize: travelieTheme.typography.fontSize['2xl'],
              fontWeight: travelieTheme.typography.fontWeight.bold,
              color: travelieTheme.colors.text.primary,
              margin: 0
            }}>
              {stats.pending}
            </p>
          </div>
        </div>

        <div style={{
          background: travelieTheme.colors.neutral.card,
          borderRadius: travelieTheme.components.statCard.borderRadius,
          padding: travelieTheme.components.statCard.padding,
          boxShadow: travelieTheme.components.statCard.shadow,
          display: 'flex',
          alignItems: 'center',
          gap: travelieTheme.spacing.lg
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: travelieTheme.components.statCard.iconBorderRadius,
            background: travelieTheme.colors.status.successLight,
            color: travelieTheme.colors.status.success,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{
              fontSize: travelieTheme.typography.fontSize.sm,
              color: travelieTheme.colors.text.secondary,
              marginBottom: '4px',
              margin: 0
            }}>
              Paid
            </p>
            <p style={{
              fontSize: travelieTheme.typography.fontSize['2xl'],
              fontWeight: travelieTheme.typography.fontWeight.bold,
              color: travelieTheme.colors.text.primary,
              margin: 0
            }}>
              {stats.paid}
            </p>
          </div>
        </div>

        <div style={{
          background: travelieTheme.colors.neutral.card,
          borderRadius: travelieTheme.components.statCard.borderRadius,
          padding: travelieTheme.components.statCard.padding,
          boxShadow: travelieTheme.components.statCard.shadow,
          display: 'flex',
          alignItems: 'center',
          gap: travelieTheme.spacing.lg
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: travelieTheme.components.statCard.iconBorderRadius,
            background: '#E9D5FF',
            color: '#8B5CF6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg style={{ width: '24px', height: '24px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{
              fontSize: travelieTheme.typography.fontSize.sm,
              color: travelieTheme.colors.text.secondary,
              marginBottom: '4px',
              margin: 0
            }}>
              Paid Commissions
            </p>
            <p style={{
              fontSize: travelieTheme.typography.fontSize['2xl'],
              fontWeight: travelieTheme.typography.fontWeight.bold,
              color: travelieTheme.colors.text.primary,
              margin: 0
            }}>
              {travelieTheme.formatCurrency(stats.paidAgent)}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: travelieTheme.colors.neutral.card,
        borderRadius: travelieTheme.components.card.borderRadius,
        boxShadow: travelieTheme.components.card.shadow,
        padding: travelieTheme.spacing.lg,
        marginBottom: travelieTheme.spacing['2xl'],
        display: 'flex',
        gap: travelieTheme.spacing.md,
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <input
          type="text"
          placeholder="Search by reservation #, guest name, or supplier..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            minWidth: '300px',
            padding: '8px 12px',
            border: `1px solid ${travelieTheme.colors.neutral.border}`,
            borderRadius: travelieTheme.components.input.borderRadius,
            fontSize: travelieTheme.typography.fontSize.sm,
            color: travelieTheme.colors.text.primary,
            background: travelieTheme.colors.neutral.card
          }}
        />
        
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            border: `1px solid ${travelieTheme.colors.neutral.border}`,
            borderRadius: travelieTheme.components.input.borderRadius,
            fontSize: travelieTheme.typography.fontSize.sm,
            background: travelieTheme.colors.neutral.card,
            color: travelieTheme.colors.text.primary
          }}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          {isIndependentAgent && <option value="processing">Processing</option>}
          <option value="paid">Paid</option>
          {!isIndependentAgent && (
            <>
              <option value="denied">Denied</option>
              <option value="on_hold">On Hold</option>
              <option value="needs_reply">Needs Reply</option>
              <option value="needs_agent_reply">Needs Agent Reply</option>
              <option value="needs_admin_reply">Needs Admin Reply</option>
            </>
          )}
        </select>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: travelieTheme.spacing.sm
        }}>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            placeholder="Start date"
            style={{
              padding: '8px 12px',
              border: `1px solid ${travelieTheme.colors.neutral.border}`,
              borderRadius: travelieTheme.components.input.borderRadius,
              fontSize: travelieTheme.typography.fontSize.sm,
              background: travelieTheme.colors.neutral.card,
              color: travelieTheme.colors.text.primary
            }}
          />
          <span style={{
            color: travelieTheme.colors.text.secondary,
            fontSize: travelieTheme.typography.fontSize.sm
          }}>to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            placeholder="End date"
            style={{
              padding: '8px 12px',
              border: `1px solid ${travelieTheme.colors.neutral.border}`,
              borderRadius: travelieTheme.components.input.borderRadius,
              fontSize: travelieTheme.typography.fontSize.sm,
              background: travelieTheme.colors.neutral.card,
              color: travelieTheme.colors.text.primary
            }}
          />
        </div>
      </div>

      {/* Commissions Table */}
      <div style={{
        background: travelieTheme.colors.neutral.card,
        borderRadius: travelieTheme.components.table.borderRadius,
        boxShadow: travelieTheme.components.card.shadow,
        overflow: 'hidden'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          background: '#FFFFFF',
          borderRadius: travelieTheme.components.card.borderRadius,
          overflow: 'hidden',
          boxShadow: travelieTheme.components.card.boxShadow
        }}>
          <thead>
            <tr style={{
              background: travelieTheme.colors.neutral.background
            }}>
              <th title="Date Submitted" style={{
                padding: travelieTheme.components.table.headerPadding,
                textAlign: 'left',
                fontSize: travelieTheme.typography.fontSize.xs,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>Date</th>
              <th title="Reservation Number" style={{
                padding: travelieTheme.components.table.headerPadding,
                textAlign: 'left',
                fontSize: travelieTheme.typography.fontSize.xs,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>Reservation</th>
              <th title="Guest Name" style={{
                padding: travelieTheme.components.table.headerPadding,
                textAlign: 'left',
                fontSize: travelieTheme.typography.fontSize.xs,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>Guest</th>
              <th title="Supplier" style={{
                padding: travelieTheme.components.table.headerPadding,
                textAlign: 'left',
                fontSize: travelieTheme.typography.fontSize.xs,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>Supplier</th>
              <th title="Travel Date" style={{
                padding: travelieTheme.components.table.headerPadding,
                textAlign: 'left',
                fontSize: travelieTheme.typography.fontSize.xs,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>Travel</th>
              <th title={isIndependentAgent ? 'Commission Amount' : 'Gross Commission'} style={{
                padding: travelieTheme.components.table.headerPadding,
                textAlign: 'left',
                fontSize: travelieTheme.typography.fontSize.xs,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>
                {isIndependentAgent ? 'Amount' : 'Gross'}
              </th>
              {!isIndependentAgent && <th title="Agent Commission" style={{
                padding: travelieTheme.components.table.headerPadding,
                textAlign: 'left',
                fontSize: travelieTheme.typography.fontSize.xs,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>Agent</th>}
              <th title="Status" style={{
                padding: travelieTheme.components.table.headerPadding,
                textAlign: 'left',
                fontSize: travelieTheme.typography.fontSize.xs,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>Status</th>
              <th title="Actions" style={{
                padding: travelieTheme.components.table.headerPadding,
                textAlign: 'center',
                fontSize: travelieTheme.typography.fontSize.xs,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
                minWidth: '180px',
                width: '180px'
              }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCommissions.length === 0 ? (
              <tr>
                <td colSpan="9" style={{
                  textAlign: 'center',
                  padding: `${travelieTheme.spacing['3xl']} ${travelieTheme.spacing.lg}`,
                  color: travelieTheme.colors.text.secondary,
                  fontSize: travelieTheme.typography.fontSize.sm
                }}>
                  {commissions.length === 0 ? (
                    <p>No commissions found</p>
                  ) : (
                    <p>No commissions match your filters</p>
                  )}
                </td>
              </tr>
            ) : (
              filteredCommissions.map(commission => (
                <tr key={commission.id} style={{
                  transition: `background ${travelieTheme.transitions.fast}`
                }} onMouseEnter={(e) => e.currentTarget.style.background = travelieTheme.colors.neutral.hover}
                   onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td data-label="Date Submitted" style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.primary,
                    borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
                  }}>
                    {travelieTheme.formatDate(commission.created_at)}
                  </td>
                  <td data-label="Reservation #" style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.primary,
                    borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
                  }}>
                    {commission.reservation_number}
                  </td>
                  <td data-label="Guest Name" style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.primary,
                    borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
                  }}>
                    {commission.guest_first_name} {commission.guest_last_name}
                  </td>
                  <td data-label="Supplier" style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.primary,
                    borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
                  }}>
                    {commission.supplier}
                  </td>
                  <td data-label="Travel Date" style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.primary,
                    borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
                  }}>
                    {travelieTheme.formatDate(commission.travel_date)}
                  </td>
                  <td data-label={isIndependentAgent ? "Commission Amount" : "Gross Commission"} style={{
                    padding: travelieTheme.components.table.cellPadding,
                    fontSize: travelieTheme.typography.fontSize.sm,
                    color: travelieTheme.colors.text.primary,
                    textAlign: 'right',
                    borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
                  }}>
                    {travelieTheme.formatCurrency(commission.gross_commission)}
                  </td>
                  {!isIndependentAgent && (
                    <td data-label="Agent Commission" style={{
                      padding: travelieTheme.components.table.cellPadding,
                      fontSize: travelieTheme.typography.fontSize.sm,
                      fontWeight: travelieTheme.typography.fontWeight.medium,
                      color: travelieTheme.colors.text.primary,
                      textAlign: 'right',
                      borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
                    }}>
                      {travelieTheme.formatCurrency(commission.agent_commission)}
                    </td>
                  )}
                  <td data-label="Status" style={{
                    padding: travelieTheme.components.table.cellPadding,
                    borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
                  }}>
                    <span style={{
                      padding: travelieTheme.components.badge.padding,
                      fontSize: travelieTheme.components.badge.fontSize,
                      fontWeight: travelieTheme.components.badge.fontWeight,
                      borderRadius: travelieTheme.components.badge.borderRadius,
                      textTransform: 'capitalize',
                      display: 'inline-block',
                      ...travelieTheme.getStatusBadgeStyle(commission.status)
                    }}>
                      {commission.status.charAt(0).toUpperCase() + commission.status.slice(1).replace('_', ' ')}
                    </span>
                  </td>
                  <td data-label="Actions" style={{
                    padding: travelieTheme.components.table.cellPadding,
                    textAlign: 'center',
                    borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
                    minWidth: '180px',
                    width: '180px'
                  }}>
                    <div style={{ display: 'flex', gap: travelieTheme.spacing.sm, justifyContent: 'center', alignItems: 'center' }}>
                      <button
                        onClick={() => {
                          if (commission.status === 'draft') {
                            // For drafts, open the edit form
                            setSelectedCommission(commission);
                            setShowForm(true);
                          } else {
                            // For non-drafts, show details modal
                            setSelectedCommission(commission);
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          fontSize: travelieTheme.typography.fontSize.sm,
                          fontWeight: travelieTheme.typography.fontWeight.medium,
                          color: travelieTheme.colors.primary.main,
                          background: 'transparent',
                          border: 'none',
                          borderRadius: travelieTheme.components.button.primary.borderRadius,
                          cursor: 'pointer',
                          transition: `all ${travelieTheme.transitions.fast}`
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#2563EB'}
                        onMouseLeave={(e) => e.currentTarget.style.color = travelieTheme.colors.primary.main}
                      >
                        {commission.status === 'draft' ? 'Edit' : 'View'}
                      </button>
                      {isIndependentAgent ? (
                        <select
                          value={commission.status}
                          onChange={(e) => handleStatusUpdate(commission.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: travelieTheme.typography.fontSize.sm,
                            border: `1px solid ${travelieTheme.colors.neutral.border}`,
                            borderRadius: travelieTheme.components.input.borderRadius,
                            padding: '4px 8px',
                            background: travelieTheme.colors.neutral.card,
                            color: travelieTheme.colors.text.primary
                          }}
                        >
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="paid">Paid</option>
                        </select>
                      ) : (
                        <button
                          onClick={() => {
                            setSelectedCommissionForMessaging(commission);
                            setShowMessagingModal(true);
                          }}
                          style={{
                            padding: '4px 8px',
                            fontSize: travelieTheme.typography.fontSize.sm,
                            fontWeight: travelieTheme.typography.fontWeight.medium,
                            color: travelieTheme.colors.status.success,
                            background: 'transparent',
                            border: 'none',
                            borderRadius: travelieTheme.components.button.primary.borderRadius,
                            cursor: 'pointer',
                            transition: `all ${travelieTheme.transitions.fast}`
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#059669'}
                          onMouseLeave={(e) => e.currentTarget.style.color = travelieTheme.colors.status.success}
                        >
                          Message
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Commission Form Modal */}
      {showForm && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: travelieTheme.spacing.md }} onClick={() => setShowForm(false)}>
          <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "600px", width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }} onClick={(e) => e.stopPropagation()}>
            <CommissionForm 
              onClose={() => {
                setShowForm(false);
                setSelectedCommission(null);
              }}
              onSuccess={handleCommissionSubmit}
              draftCommission={selectedCommission?.status === 'draft' ? selectedCommission : null}
            />
          </div>
        </div>
      )}

      {/* Tax Forms Modal - Hidden from independent agents */}
      {showTaxForms && user?.role !== 'independent_agent' && (
        <TaxForms 
          agentId={user?.id} 
          onClose={() => setShowTaxForms(false)} 
        />
      )}

      {/* Commission Messaging Modal */}
      {showMessagingModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: travelieTheme.spacing.md }} onClick={() => setShowMessagingModal(false)}>
          <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "600px", width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }} onClick={(e) => e.stopPropagation()}>
            <CommissionMessagingModal 
              commission={selectedCommissionForMessaging}
              onClose={() => setShowMessagingModal(false)}
              onMessageSent={handleMessageSent}
            />
          </div>
        </div>
      )}

      {/* Commission Details Modal - Only for non-draft commissions */}
      {selectedCommission && selectedCommission.status !== 'draft' && !showForm && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: travelieTheme.spacing.md }} onClick={() => setSelectedCommission(null)}>
          <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "800px", width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3>Commission Details</h3>
              <button 
                onClick={() => setSelectedCommission(null)}
                style={{ background: "none", border: "none", fontSize: "28px", cursor: "pointer", color: travelieTheme.colors.text.secondary, padding: 0, width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <svg style={{ width: "24px", height: "24px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div style={{ padding: travelieTheme.spacing.lg }}>
              <div style={{ marginBottom: travelieTheme.spacing.lg }}>
                <h4>Booking Information</h4>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Reservation Number:</span>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary }}>{selectedCommission.reservation_number}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Supplier:</span>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary }}>{selectedCommission.supplier}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Booking Date:</span>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary }}>{formatDate(selectedCommission.booking_date)}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Travel Date:</span>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary }}>{formatDate(selectedCommission.travel_date)}</span>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: travelieTheme.spacing.lg }}>
                <h4>Guest Information</h4>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Guest Name:</span>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary }}>{selectedCommission.guest_first_name} {selectedCommission.guest_last_name}</span>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: travelieTheme.spacing.lg }}>
                <h4>Commission Details</h4>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginTop: travelieTheme.spacing.md }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Gross Commission:</span>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary }}>{formatCurrency(selectedCommission.gross_commission)}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Agent Commission:</span>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.lg, color: travelieTheme.colors.status.success, fontWeight: travelieTheme.typography.fontWeight.semibold }}>{formatCurrency(selectedCommission.agent_commission)}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Status:</span>
                    <span style={getStatusBadgeStyle(selectedCommission.status)}>
                      {selectedCommission.status.charAt(0).toUpperCase() + selectedCommission.status.slice(1)}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.medium }}>Submitted:</span>
                    <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary }}>{formatDate(selectedCommission.created_at)}</span>
                  </div>
                </div>
              </div>

              {selectedCommission.notes && (
                <div style={{ marginBottom: travelieTheme.spacing.lg }}>
                  <h4>Agent Notes</h4>
                  <p style={{ padding: travelieTheme.spacing.md, background: "#F9FAFB", borderRadius: travelieTheme.components.card.borderRadius, fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary, whiteSpace: "pre-wrap" }}>{selectedCommission.notes}</p>
                </div>
              )}

              {selectedCommission.admin_notes && (
                <div style={{ marginBottom: travelieTheme.spacing.lg }}>
                  <h4>Admin Notes</h4>
                  <p style={{ padding: travelieTheme.spacing.md, background: "#FEF3C7", border: "1px solid #FDE047", borderRadius: travelieTheme.components.card.borderRadius, fontSize: travelieTheme.typography.fontSize.sm, color: "#92400E", whiteSpace: "pre-wrap" }}>{selectedCommission.admin_notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionsList;
